import React from "react";

export const COMPANY_LOGO_SIZE = 512;
export const COMPANY_LOGO_FILE_SVG = "puzzle-studio-logo-512x512.svg";
export const COMPANY_LOGO_FILE_PNG = "puzzle-studio-logo-512x512.png";

/** pad 4 + 3×168 + 0 gap = 512 (no gaps between cells) */
const PAD = 4;
const GAP = 0;
const TILE = 168;
const STRIDE = TILE + GAP;

function xy(row, col) {
    return { x: PAD + col * STRIDE, y: PAD + row * STRIDE };
}

function CrosswordLines({ x, y }) {
    // 2×2 sub-grid: one horizontal + one vertical midline
    const mid = TILE / 2;
    return (
        <g>
            <line
                x1={x}
                y1={y + mid}
                x2={x + TILE}
                y2={y + mid}
                stroke="#FFFFFF"
                strokeOpacity="0.55"
                strokeWidth="4"
            />
            <line
                x1={x + mid}
                y1={y}
                x2={x + mid}
                y2={y + TILE}
                stroke="#FFFFFF"
                strokeOpacity="0.55"
                strokeWidth="4"
            />
        </g>
    );
}

/**
 * Classic rounded jigsaw edge (tab / blank) along one side.
 * kind: 1 = outward tab, -1 = inward blank, 0 = flat.
 */
function roundJigsawEdge(x, y, w, h, side, kind) {
    const neck = w * (16 / 124);
    const depth = w * (20 / 124);

    if (kind === 0) {
        if (side === "top") return `L ${x + w} ${y}`;
        if (side === "right") return `L ${x + w} ${y + h}`;
        if (side === "bottom") return `L ${x} ${y + h}`;
        return `L ${x} ${y}`;
    }

    const out = kind;
    if (side === "top") {
        const cx = x + w / 2;
        const dy = -out * depth;
        return [
            `L ${cx - neck} ${y}`,
            `C ${cx - neck * 0.2} ${y}, ${cx - depth * 0.95} ${y + dy * 0.15}, ${cx - depth * 0.85} ${y + dy * 0.55}`,
            `C ${cx - depth * 0.7} ${y + dy}, ${cx - depth * 0.35} ${y + dy}, ${cx} ${y + dy}`,
            `C ${cx + depth * 0.35} ${y + dy}, ${cx + depth * 0.7} ${y + dy}, ${cx + depth * 0.85} ${y + dy * 0.55}`,
            `C ${cx + depth * 0.95} ${y + dy * 0.15}, ${cx + neck * 0.2} ${y}, ${cx + neck} ${y}`,
            `L ${x + w} ${y}`,
        ].join(" ");
    }
    if (side === "right") {
        const cy = y + h / 2;
        const dx = out * depth;
        return [
            `L ${x + w} ${cy - neck}`,
            `C ${x + w} ${cy - neck * 0.2}, ${x + w + dx * 0.15} ${cy - depth * 0.95}, ${x + w + dx * 0.55} ${cy - depth * 0.85}`,
            `C ${x + w + dx} ${cy - depth * 0.7}, ${x + w + dx} ${cy - depth * 0.35}, ${x + w + dx} ${cy}`,
            `C ${x + w + dx} ${cy + depth * 0.35}, ${x + w + dx} ${cy + depth * 0.7}, ${x + w + dx * 0.55} ${cy + depth * 0.85}`,
            `C ${x + w + dx * 0.15} ${cy + depth * 0.95}, ${x + w} ${cy + neck * 0.2}, ${x + w} ${cy + neck}`,
            `L ${x + w} ${y + h}`,
        ].join(" ");
    }
    if (side === "bottom") {
        const cx = x + w / 2;
        const dy = out * depth;
        return [
            `L ${cx + neck} ${y + h}`,
            `C ${cx + neck * 0.2} ${y + h}, ${cx + depth * 0.95} ${y + h + dy * 0.15}, ${cx + depth * 0.85} ${y + h + dy * 0.55}`,
            `C ${cx + depth * 0.7} ${y + h + dy}, ${cx + depth * 0.35} ${y + h + dy}, ${cx} ${y + h + dy}`,
            `C ${cx - depth * 0.35} ${y + h + dy}, ${cx - depth * 0.7} ${y + h + dy}, ${cx - depth * 0.85} ${y + h + dy * 0.55}`,
            `C ${cx - depth * 0.95} ${y + h + dy * 0.15}, ${cx - neck * 0.2} ${y + h}, ${cx - neck} ${y + h}`,
            `L ${x} ${y + h}`,
        ].join(" ");
    }
    // left
    const cy = y + h / 2;
    const dx = -out * depth;
    return [
        `L ${x} ${cy + neck}`,
        `C ${x} ${cy + neck * 0.2}, ${x + dx * 0.15} ${cy + depth * 0.95}, ${x + dx * 0.55} ${cy + depth * 0.85}`,
        `C ${x + dx} ${cy + depth * 0.7}, ${x + dx} ${cy + depth * 0.35}, ${x + dx} ${cy}`,
        `C ${x + dx} ${cy - depth * 0.35}, ${x + dx} ${cy - depth * 0.7}, ${x + dx * 0.55} ${cy - depth * 0.85}`,
        `C ${x + dx * 0.15} ${cy - depth * 0.95}, ${x} ${cy - neck * 0.2}, ${x} ${cy - neck}`,
        `L ${x} ${y}`,
    ].join(" ");
}

/** Classic rounded jigsaw piece path (clockwise from top-left). */
export function roundJigsawPath(x, y, w, h, edges) {
    return [
        `M ${x} ${y}`,
        roundJigsawEdge(x, y, w, h, "top", edges.top),
        roundJigsawEdge(x, y, w, h, "right", edges.right),
        roundJigsawEdge(x, y, w, h, "bottom", edges.bottom),
        roundJigsawEdge(x, y, w, h, "left", edges.left),
        "Z",
    ].join(" ");
}

/**
 * Premium 512×512 multi-genre puzzle studio logo.
 * Light silver canvas · sharp 3×3 grid · warm 2×2 + cool jigsaw 2×2.
 *
 * Layout:
 *   W W S
 *   W J J
 *   S J J
 * Center (1,1) is shared: green tile with jigsaw cut (bridges both 2×2 blocks).
 */
export default function CompanyLogoSvg({ idPrefix = "studioLogo" }) {
    const id = (name) => `${idPrefix}-${name}`;
    const shadow = `url(#${id("shadow")})`;

    // Top-left 2×2 warm (center cell rendered as jigsaw piece below)
    const warm = [
        [0, 0, "warmRed"],
        [0, 1, "warmOrange"],
        [1, 0, "warmYellow"],
    ];

    // Remaining corners — slate with 2×2 etched sub-grid
    const etchedSlate = [
        [0, 2],
        [2, 0],
    ];

    // Bottom-right 2×2 — classic rounded interlocking jigsaw
    // (1,1) uses warmGreen so the top-left rainbow 2×2 stays complete
    const coolJigsaw = [
        {
            key: "cj-1-1",
            grad: "warmGreen",
            d: roundJigsawPath(xy(1, 1).x, xy(1, 1).y, TILE, TILE, {
                top: 0,
                right: 1,
                bottom: 1,
                left: 0,
            }),
        },
        {
            key: "cj-1-2",
            grad: "coolBlue",
            d: roundJigsawPath(xy(1, 2).x, xy(1, 2).y, TILE, TILE, {
                top: 0,
                right: 0,
                bottom: -1,
                left: -1,
            }),
        },
        {
            key: "cj-2-1",
            grad: "coolPurple",
            d: roundJigsawPath(xy(2, 1).x, xy(2, 1).y, TILE, TILE, {
                top: -1,
                right: 1,
                bottom: 0,
                left: 0,
            }),
        },
        {
            key: "cj-2-2",
            grad: "coolCyan",
            d: roundJigsawPath(xy(2, 2).x, xy(2, 2).y, TILE, TILE, {
                top: 1,
                right: 0,
                bottom: 0,
                left: -1,
            }),
        },
    ];

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={COMPANY_LOGO_SIZE}
            height={COMPANY_LOGO_SIZE}
            viewBox="0 0 512 512"
            role="img"
            aria-label="Multi-genre puzzle studio logo"
        >
            <defs>
                <linearGradient id={id("bg")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FCFCFD" />
                    <stop offset="100%" stopColor="#E4E7EB" />
                </linearGradient>

                <linearGradient id={id("warmRed")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#F87171" />
                    <stop offset="100%" stopColor="#DC2626" />
                </linearGradient>
                <linearGradient id={id("warmOrange")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FB923C" />
                    <stop offset="100%" stopColor="#EA580C" />
                </linearGradient>
                <linearGradient id={id("warmYellow")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FDE047" />
                    <stop offset="100%" stopColor="#CA8A04" />
                </linearGradient>
                <linearGradient id={id("warmGreen")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#4ADE80" />
                    <stop offset="100%" stopColor="#16A34A" />
                </linearGradient>

                <linearGradient id={id("coolBlue")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#38BDF8" />
                    <stop offset="100%" stopColor="#0284C7" />
                </linearGradient>
                <linearGradient id={id("coolPurple")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#C084FC" />
                    <stop offset="100%" stopColor="#7C3AED" />
                </linearGradient>
                <linearGradient id={id("coolCyan")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#A5F3FC" />
                    <stop offset="100%" stopColor="#0E7490" />
                </linearGradient>

                <linearGradient id={id("slate")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#94A3B8" />
                    <stop offset="45%" stopColor="#64748B" />
                    <stop offset="100%" stopColor="#1E293B" />
                </linearGradient>

                <filter
                    id={id("shadow")}
                    x="-30%"
                    y="-30%"
                    width="160%"
                    height="160%"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodColor="#0F172A" floodOpacity="0.18" result="flood" />
                    <feComposite in="flood" in2="SourceAlpha" operator="in" result="mask" />
                    <feOffset in="mask" dx="3" dy="3" result="offset" />
                    <feMerge>
                        <feMergeNode in="offset" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>

            <rect width="512" height="512" fill={`url(#${id("bg")})`} />

            <rect
                x="2"
                y="2"
                width="508"
                height="508"
                fill="none"
                stroke="#64748B"
                strokeWidth="4"
                shapeRendering="crispEdges"
            />

            {/* Sliding — warm rainbow (top-left 2×2, minus shared center) */}
            {warm.map(([r, c, grad]) => {
                const { x, y } = xy(r, c);
                return (
                    <rect
                        key={`w-${r}-${c}`}
                        x={x}
                        y={y}
                        width={TILE}
                        height={TILE}
                        fill={`url(#${id(grad)})`}
                        filter={shadow}
                        shapeRendering="crispEdges"
                    />
                );
            })}

            {/* Slate corners — 2×2 etched sub-grid + visible diagonal gradient */}
            {etchedSlate.map(([r, c]) => {
                const { x, y } = xy(r, c);
                return (
                    <g key={`es-${r}-${c}`}>
                        <rect
                            x={x}
                            y={y}
                            width={TILE}
                            height={TILE}
                            fill={`url(#${id("slate")})`}
                            filter={shadow}
                            shapeRendering="crispEdges"
                        />
                        <CrosswordLines x={x} y={y} />
                    </g>
                );
            })}

            {/* Jigsaw — bottom-right 2×2 (shared center = warm green) */}
            {coolJigsaw.map((piece) => (
                <path
                    key={piece.key}
                    d={piece.d}
                    fill={`url(#${id(piece.grad)})`}
                    filter={shadow}
                    stroke="#0F172A"
                    strokeOpacity="0.22"
                    strokeWidth="1.25"
                    strokeLinejoin="round"
                />
            ))}
        </svg>
    );
}
